import styles from "./444dle.module.css";
import {
  bestEval,
  dailySolutions,
  EMOJIS,
  evaluate,
  NUM_GUESSES,
  NUM_LETTERS,
  NUM_WORDS,
  SOLVED_EMOJI,
} from "./utils";
import { requestDay, vibrate } from "../utils";

<lazy-word-list/validWords/>

<const/day=requestDay($global.request)>
<const/solutions=dailySolutions(day)>

<let/guesses=[] as string[]>
<let/pending="">

<const/boards=solutions.map((solution) => {
  const rows = guesses.map((guess) => ({
    guess,
    scores: evaluate(guess, solution),
  }));
  const wonAt = rows.findIndex(({ scores }) =>
    scores.every((score) => score === "bull"),
  );
  return {
    solution,
    wonAt: wonAt === -1 ? undefined : wonAt,
    played: wonAt === -1 ? rows : rows.slice(0, wonAt + 1),
  };
})>

<const/won=boards.every(({ wonAt }) => wonAt !== undefined)>
<const/finished=won || guesses.length >= NUM_GUESSES>

<let/helpOpen=false>
<let/resultsOpen=false>

<header class=styles.header>
  <h1>444dle</h1>
  <span class=styles.headerActions>
    <button
      class=styles.iconButton
      title="help"
      onClick() {
        helpOpen = true;
      }
    >
      ?
    </button>
    <if=finished>
      <button
        class=styles.iconButton
        title="show results"
        onClick() {
          resultsOpen = true;
        }
      >
        ⇪
      </button>
    </if>
  </span>
</header>

<game-dialog open:=helpOpen>
  <@heading>Three words, one keyboard</@heading>
  <p>Every guess is played against all three words at once.</p>
  <p>
    <strong class=styles.bull>Green</strong> is the right letter in the right
    spot, <strong class=styles.cow>orange</strong> is the right letter somewhere
    else, grey is not in the word at all.
  </p>
  <p>A key is striped with its colour for each word, in order.</p>
  <p>You get ${NUM_GUESSES} guesses for all ${NUM_WORDS} words.</p>
</game-dialog>

<div class=styles.boards>
  <for|{ played, wonAt }, i| of=boards>
    <board/boardActions
      index=i
      played=played
      pending=wonAt === undefined ? pending : ""
      pendingAt=guesses.length
    />
  </for>
</div>

<const/submit() {
  if (!validWords || pending.length < NUM_LETTERS) return;
  if (!validWords.has(pending)) {
    for (const board of boardActions) board.shake();
    vibrate([100, 100, 50, 100, 100]);
    return;
  }
  guesses = [...guesses, pending];
  pending = "";
  vibrate(50);
}>

<const/keyColors=Object.fromEntries(
  [...new Set(guesses.join(""))].map((letter) => [
    letter,
    boards.map(({ played }) =>
      bestEval(
        played.flatMap(({ guess, scores }) =>
          scores.filter((_, i) => guess[i] === letter),
        ),
      ),
    ),
  ]),
)>

<keyboard keyColors=keyColors/>

<const/score=(
  boards
    .map(({ wonAt }) => wonAt ?? Infinity)
    .sort((a, b) => b - a)
    .map((wonAt) => (wonAt === Infinity ? "X" : wonAt + 1))
    .join("")
)>

<game-dialog open:=resultsOpen>
  <@heading>
    ${day.toLocaleDateString("en-US", { dateStyle: "long", timeZone: "UTC" })}
  </@heading>
  <p class=styles.stat>${[...score].join(" ")}</p>
  <p class=styles.answers>
    <span class=styles.srOnly>Answers:</span>
    <for|{ solution }| of=boards><span>${solution}</span></for>
  </p>

  <const/block(indexes: number[], indent = "") {
    const group = indexes.map((i) => boards[i]);
    const height = Math.max(...group.map(({ played }) => played.length));
    return Array.from(
      { length: height },
      (_, row) =>
        indent +
        group
          .map(
            ({ played }) =>
              played[row]?.scores.map((score) => EMOJIS[score]).join("") ??
              SOLVED_EMOJI.repeat(NUM_LETTERS),
          )
          .join("  "),
    ).join("\n");
  }>
  <share-button text=(
    `444dle ${day.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "2-digit",
      timeZone: "UTC",
    })}: ${score}

${block([0, 1])}

${block([2], "           ")}`
  )/>
</game-dialog>

<script>
  if (finished) {
    // 300ms is the per-column stagger in the stylesheet; the rest is one turn
    // plus a beat, so the results do not cut the reveal off.
    const reveal = setTimeout(
      () => (resultsOpen = true),
      (NUM_LETTERS - 1) * 300 + 800,
    );
    $signal.onabort = () => clearTimeout(reveal);
  }
</script>

<script>
  document.addEventListener(
    "keydown",
    (e) => {
      if (finished || e.altKey || e.ctrlKey || e.metaKey) return;
      if (!/^(Enter|Backspace|[a-zA-Z])$/.test(e.key)) return;
      if (document.querySelector("dialog[open]")) return;
      e.preventDefault();
      if (e.key === "Enter") {
        submit();
      } else if (e.key === "Backspace") {
        pending = pending.slice(0, -1);
      } else if (pending.length < NUM_LETTERS) {
        pending += e.key.toLowerCase();
      }
    },
    { signal: $signal },
  );
</script>
